import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import {
  Supplier,
  SupplierCreate,
  SupplierCreateInline,
  SupplierCreateResult,
  SupplierDetail,
  SupplierDropdown,
  SupplierUpdate,
} from '@core/types/supplier.type';
import { SupplierService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class SupplierQuery {
  private readonly service = inject(SupplierService);
  private readonly queryClient = inject(QueryClient);

  useSuppliersGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.suppliers.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<Supplier>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useActiveSuppliers() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.suppliers.all,
      queryFn: () => lastValueFrom(this.service.getActiveSuppliers()),
      select: (res: ApiResponse<SupplierDropdown[]>) => res.data,
      staleTime: environment.staleTime,
    }));
  }

  useCanCreateSupplier() {
    return injectQuery(() => ({
      queryKey: [...QUERY_KEYS.suppliers.all, 'can-create'],
      queryFn: () => lastValueFrom(this.service.canCreate()),
      select: (res: ApiResponse<boolean>) => res.data,
      staleTime: environment.staleTime,
    }));
  }

  useSupplier(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: QUERY_KEYS.suppliers.detail(v || ''),
        queryFn: () => lastValueFrom(this.service.get(v!)),
        select: (res: ApiResponse<Supplier>) => res.data,
        enabled: !!v,
      };
    });
  }

  useSupplierDetail(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: [...QUERY_KEYS.suppliers.detail(v || ''), 'full'],
        queryFn: () => lastValueFrom(this.service.getDetail(v!)),
        select: (res: ApiResponse<SupplierDetail>) => res.data,
        enabled: !!v,
      };
    });
  }

  useCreateSupplier() {
    return injectMutation(() => ({
      mutationFn: (data: SupplierCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.suppliers.all }),
    }));
  }

  useCreateSupplierInline() {
    return injectMutation(() => ({
      mutationFn: (data: SupplierCreateInline) => lastValueFrom(this.service.createInline(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.suppliers.all }),
    }));
  }

  useUpdateSupplier() {
    return injectMutation(() => ({
      mutationFn: (data: SupplierUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.suppliers.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.suppliers.all });
      },
    }));
  }

  useDeleteSupplier() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.suppliers.all }),
    }));
  }
}
